import React from 'react';

import { InputProps } from './Commons';

import './Autocomplete.scss';
import './Input.scss';

export type AutocompleteProps = {
  fetcher: (query: string) => Promise<Option[]>;
  onPick: (option: Option | undefined) => void;
};

export type Option = { label: string; value: string };
export const Autocomplete = React.forwardRef<
  any,
  InputProps & AutocompleteProps
>((props: InputProps & AutocompleteProps, ref?: any) => {
  const { className, onPick, fetcher, ...otherProps } = props;

  const [showDropdown, setShowDropdown] = React.useState(false);
  const [suggestions, setSuggestions] = React.useState<Option[]>([]);

  const handleDocumentClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    setShowDropdown(false);
  };

  React.useEffect(() => {
    if (showDropdown) {
      document.addEventListener('click', handleDocumentClick);
    } else {
      document.removeEventListener('click', handleDocumentClick);
    }
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  }, [showDropdown]);

  const doFetchSuggestions = async (query: string) => {
    const suggestions = await fetcher(query);
    setSuggestions(suggestions);
    if (suggestions.length > 0) {
      setShowDropdown(true);
    }
  };

  const filteredOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      setShowDropdown(false);
      onPick(suggestions[0]);
    }

    if (otherProps.onKeyDown) {
      otherProps.onKeyDown(event);
    }
    // Fetch suggestions
    doFetchSuggestions(event.currentTarget.value);
  };

  const intOnPick = (option: Option) => {
    setShowDropdown(false);
    onPick(option);
  };

  const [inputValue, setInputValue] = React.useState('');

  React.useEffect(() => {
    const value = props.value;
    if (value === '') {
      setInputValue('');
    }
    if (value && typeof value === 'string') {
      setInputValue(value);
    }
  }, [props.value]);

  const intSetInputValue = (value: string) => {
    // Unpick
    onPick(undefined);
    setInputValue(value);
  };

  return (
    <div className={`input-autocomplete ${className}`}>
      <div className='input-wrapper'>
        <input
          className={`input ${className || ''}`}
          ref={ref}
          onKeyUp={filteredOnKeyDown}
          {...otherProps}
          value={inputValue}
          onChange={({ target }) => intSetInputValue(target.value)}
        />
      </div>
      <AutocompleteList
        visible={showDropdown}
        options={suggestions}
        onPick={intOnPick}
      />
    </div>
  );
});

export const AutocompleteList = ({
  visible,
  options,
  onPick,
}: {
  visible: boolean;
  options: Option[];
  onPick: (option: Option) => void;
}) => {
  return (
    <div className={`autocomplete-list ${visible ? 'visible' : ''}`}>
      {options.map((o) => (
        <div
          className='autocomplete-list-item'
          onClick={() => onPick(o)}
          key={`autocomplete-list-${o.value}`}
        >
          <div className='autocomplete-list-item-label'>{o.label}</div>
        </div>
      ))}
    </div>
  );
};
